import { useCallback, useState } from 'react';
import type { ChatMessage } from '../components/chat/types';

interface UseDeployChatStreamOptions {
  token: string;
  deploySlug: string | null;
  onAppend: (msg: ChatMessage) => void;
  onUpdateLast: (updater: (previous: ChatMessage) => ChatMessage) => void;
}

interface UseDeployChatStreamResult {
  isStreaming: boolean;
  streamResponse: (history: ChatMessage[], userMessage: string) => Promise<void>;
}

const SSE_DATA_PREFIX = 'data: ';

/**
 * SSE streaming client for the deploy chat. Handles POSTing the conversation
 * history, reading the event stream, parsing each `data:` line as JSON, and
 * delegating message appends/updates to the caller.
 */
export function useDeployChatStream(options: UseDeployChatStreamOptions): UseDeployChatStreamResult {
  const [isStreaming, setIsStreaming] = useState(false);

  const streamResponse = useCallback(async (history: ChatMessage[], userMessage: string): Promise<void> => {
    setIsStreaming(true);
    const allMessages = [...history, { role: 'user' as const, content: userMessage }];
    try {
      const response = await postChat(options.token, allMessages, options.deploySlug);
      if (!response.ok || !response.body) {
        options.onAppend({ role: 'assistant', content: 'Sorry, something went wrong. Please try again.', type: 'error' });
        return;
      }
      await consumeStream(response.body, options.onAppend, options.onUpdateLast);
    } catch (err) {
      options.onAppend({
        role: 'assistant',
        content: `Connection error: ${err instanceof Error ? err.message : 'unknown'}. Please try again.`,
        type: 'error',
      });
    } finally {
      setIsStreaming(false);
    }
  }, [options]);

  return { isStreaming, streamResponse };
}

/** POST the conversation to the chat endpoint and return the fetch response. */
async function postChat(
  token: string,
  messages: ChatMessage[],
  slug: string | null,
): Promise<Response> {
  return fetch('/api/apps/chat', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({
      messages: messages.map(m => ({ role: m.role, content: m.content })),
      slug,
    }),
  });
}

/** Pump a ReadableStream, extracting and applying each SSE event. */
async function consumeStream(
  body: ReadableStream<Uint8Array>,
  append: UseDeployChatStreamOptions['onAppend'],
  updateLast: UseDeployChatStreamOptions['onUpdateLast'],
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let accumulated = '';
  let assistantActive = false;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    const chunk = decoder.decode(value, { stream: true });
    for (const event of splitSseEvents(chunk)) {
      const payload = parseEvent(event);
      if (!payload) continue;
      if (payload.type !== 'text') continue;
      accumulated += payload.content;
      if (!assistantActive) {
        append({ role: 'assistant', content: accumulated });
        assistantActive = true;
      } else {
        updateLast(prev =>
          prev.role === 'assistant' && prev.type !== 'progress'
            ? { role: 'assistant', content: accumulated }
            : prev,
        );
      }
    }
  }
}

/** Split an SSE chunk into raw event lines. */
function splitSseEvents(chunk: string): string[] {
  return chunk.split('\n');
}

/** Parse one SSE `data:` line into its JSON payload, or null if not parseable. */
function parseEvent(line: string): { type: string; content?: string } | null {
  if (!line.startsWith(SSE_DATA_PREFIX)) return null;
  const body = line.substring(SSE_DATA_PREFIX.length);
  try {
    return JSON.parse(body) as { type: string; content?: string };
  } catch {
    return null;
  }
}
